import { artifactCodec } from "@/navigation/apps/artifactLocation";
import { stripTransientUrlParams } from "@/navigation/urlTransient";
import { getAppDefinition } from "./appRegistryAccess";
import type { OsWindow } from "./OsContext";
import { buildWindowUrl, type WindowUrlConfig } from "./urlMapping";

export type WindowKeyFn = (params: Record<string, string>) => string | null;

const DEFAULT_SINGLETON_WHEN_NO_ID = new Set([
  "home",
  "dashboard",
  "reports",
  "inbox",
]);

export function resolveWindowKey(
  appId: string,
  params: Record<string, string>,
): string | null {
  const app = getAppDefinition(appId);
  if (!app) {
    return null;
  }
  if (app.windowKey) {
    return app.windowKey(params);
  }
  if (app.isSingleton) {
    return appId;
  }
  const id = params.id?.trim();
  if (id) {
    return `${appId}:${id}`;
  }
  if (DEFAULT_SINGLETON_WHEN_NO_ID.has(appId)) {
    return appId;
  }
  return null;
}

export function isWorkspaceScopedApp(appId: string): boolean {
  return getAppDefinition(appId)?.windowScope === "workspace";
}

export function findExistingWindowForOpen(
  windows: readonly OsWindow[],
  appId: string,
  params: Record<string, string>,
  activeWorkspaceId: string,
): OsWindow | undefined {
  const key = resolveWindowKey(appId, params);
  if (key === null) {
    return undefined;
  }

  if (isWorkspaceScopedApp(appId)) {
    return windows.find(
      (w) =>
        w.appId === appId &&
        w.workspaceId === activeWorkspaceId &&
        resolveWindowKey(appId, w.params) === key,
    );
  }

  return windows.find(
    (w) => w.appId === appId && resolveWindowKey(appId, w.params) === key,
  );
}

/** Window in the workspace whose canonical URL is exactly `url` (e.g. restored window after reload). */
export function findWindowShowingUrl(
  windows: readonly OsWindow[],
  url: string,
  workspaceId: string,
): OsWindow | undefined {
  return windows.find(
    (w) => w.workspaceId === workspaceId && buildWindowUrl(w.appId, w.params) === url,
  );
}

export function hasOpenDeepLinkWindow(
  windows: readonly OsWindow[],
  windowConfig: WindowUrlConfig,
  workspaceId?: string,
): boolean {
  const candidates = windows.filter(
    (window) =>
      !window.isMinimized &&
      (workspaceId == null || window.workspaceId === workspaceId),
  );
  const activeWorkspaceId = workspaceId ?? "";
  return (
    findExistingWindowForOpen(
      candidates,
      windowConfig.appId,
      windowConfig.params,
      activeWorkspaceId,
    ) !== undefined
  );
}

/** Canonical artefact window params so URL sync and singleton reuse stay stable. */
export function normalizeArtifactWindowParams(
  params: Record<string, string>,
): Record<string, string> {
  const next = stripTransientUrlParams({ ...params });
  const loc = artifactCodec.fromParams(next);
  if (!loc.id || loc.id === "unknown") {
    return next;
  }
  if (!loc.mode) {
    loc.mode = "read";
  }
  return artifactCodec.toParams(loc);
}

export function applyReopenParamEvents(
  appId: string,
  params: Record<string, string>,
): Record<string, string> {
  const next = stripTransientUrlParams({ ...params });
  if (appId === "meetings" && next.meetingId && !next.navSeq) {
    next.navSeq = String(Date.now());
  }
  if (appId === "artifact") {
    return normalizeArtifactWindowParams(next);
  }
  return next;
}

export function shouldBumpFocusNonce(
  params: Record<string, string>,
): boolean {
  return Boolean(params.focus?.trim());
}
